import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, ShieldCheck, RefreshCw } from "lucide-react";
import MediaUploader from "../components/MediaUploader";
import { uploadFile } from "../lib/upload";
import { useDraft } from "../lib/useDraft";
import { DraftBar } from "../components/DraftBar";
import DraftsPanel from "../components/editor/DraftsPanel";
import BackButton from "../components/BackButton";
import { BACKEND_URL } from "../lib/api";

const NICHES = ["Fashion", "Beauty", "Fitness", "Food", "Travel", "Tech", "Gaming", "Lifestyle", "Startups", "Product Reviews", "AI"];
const PLATFORMS = ["instagram", "tiktok", "youtube", "twitter"];

export default function ProfileEditor() {
  const { user, profile, refresh, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [serverForm, setServerForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [authResult, setAuthResult] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const syncFollowers = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/creators/me/sync-instagram");
      setForm((f) => ({ ...f, follower_count: data.followers }));
      await refresh();
      toast.success(`Synced ${data.followers.toLocaleString()} followers from ${data.platform}`);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      if (msg?.includes("Connect Instagram")) {
        toast.error("Connect Instagram in Growth AI first — one tap over there.");
      } else {
        toast.error(msg || "Sync failed");
      }
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (profile && profile.authenticity_score !== undefined) {
      setAuthResult({
        score: profile.authenticity_score,
        flag: profile.authenticity_flag,
        label: profile.authenticity_label,
      });
    }
  }, [profile]);

  const runAuthenticityCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/creators/me/check-authenticity");
      setAuthResult(data);
      await refresh();
      toast.success(`Audience checked · ${data.label} (${data.score}/100)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "creator") { navigate("/brand"); return; }
    if (profile) {
      const base = {
        tagline: profile.tagline || "",
        bio: profile.bio || "",
        location: profile.location || "",
        follower_count: profile.follower_count || 0,
        niches: profile.niches || [],
        platforms: profile.platforms || [],
        avatar_url: profile.avatar_url || "",
        cover_url: profile.cover_url || "",
        portfolio: profile.portfolio || [],
        social_handles: profile.social_handles || { instagram: "", tiktok: "", youtube: "", x: "" },
        languages: profile.languages || [],
        rate_card: profile.rate_card || [],
      };
      setServerForm(base);
      setForm(base);
    }
  }, [user, profile, navigate, loading]);

  const draft = useDraft("profile-editor", {
    value: form,
    enabled: !!form,
    sync: true,
    isEmpty: (v) => !v || JSON.stringify(v) === JSON.stringify(serverForm),
    onRestore: (v) => setForm((f) => ({ ...f, ...v })),
  });

  if (!form) return null;

  const toggle = (list, item) =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setSocial = (platform, value) =>
    set("social_handles", { ...form.social_handles, [platform]: value });

  const addPortfolioUrl = () => {
    const u = window.prompt("Portfolio media URL (paste an https:// image/video link)");
    if (u) set("portfolio", [...form.portfolio, u]);
  };

  /** Read an image's native dimensions from a File. */
  const readImageDimensions = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ w: 0, h: 0 }); };
    img.src = url;
  });

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Warn on low-res sources — avatar is rendered up to 240px on profile page, ~120px @2x DPR = 240px minimum
    const { w, h } = await readImageDimensions(file);
    if (w > 0 && (w < 512 || h < 512)) {
      const proceed = window.confirm(
        `Heads up — this image is ${w}×${h}px, which will look pixelated at the sizes we display avatars (up to 1080×1080).\n\n` +
        `Recommended: 1080×1080 square.\n\nUpload anyway?`
      );
      if (!proceed) { e.target.value = ""; return; }
    }
    try {
      const data = await uploadFile(file, { label: "avatar" });
      set("avatar_url", data.url);
    } catch { /* toast shown by uploadFile */ }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { w, h } = await readImageDimensions(file);
    if (w > 0 && (w < 1600 || h < 500)) {
      const proceed = window.confirm(
        `Heads up — this cover is ${w}×${h}px, which will look pixelated across a full-width profile header.\n\n` +
        `Recommended: 1920×640 landscape.\n\nUpload anyway?`
      );
      if (!proceed) { e.target.value = ""; return; }
    }
    try {
      const data = await uploadFile(file, { label: "cover" });
      set("cover_url", data.url);
    } catch { /* toast shown by uploadFile */ }
  };

  const removePortfolio = (i) => set("portfolio", form.portfolio.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        follower_count: Number(form.follower_count) || 0,
      };
      await api.put("/creators/me", payload);
      draft.clear();
      setServerForm(form);
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/creator" label="Back to dashboard" />
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pb-10 pt-4 space-y-8">
        <DraftBar draft={draft} onStartFresh={async () => { await draft.clear(); setForm(serverForm); toast("Reverted to your saved profile"); }} />
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Profile builder</div>
            <h1 className="font-display font-bold text-4xl tracking-tighter">Your public profile</h1>
          </div>
          <Button onClick={save} disabled={saving} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="profile-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <section className="glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="authenticity-section">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Audience authenticity</div>
            <div className="font-display font-bold text-xl mb-1">
              {authResult ? (
                <>
                  {authResult.label} · <span className={
                    authResult.flag === "clean" ? "text-emerald-300" :
                    authResult.flag === "suspicious" ? "text-nexus-pink" : "text-yellow-300"
                  }>{authResult.score}/100</span>
                </>
              ) : "Not checked yet"}
            </div>
            <p className="text-xs text-zinc-400">
              Nexus runs a heuristic audit across followers, engagement, portfolio, verifications and reviews.
              Brands see this score on your public profile — high scores unlock more premium briefs.
            </p>
          </div>
          <Button
            onClick={runAuthenticityCheck}
            disabled={checking}
            variant="outline"
            className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full h-11"
            data-testid="run-authenticity-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            {checking ? "Checking…" : "Run audience check"}
          </Button>
        </section>

        <section className="glass rounded-2xl p-6 space-y-5">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Identity</div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Avatar <span className="text-zinc-600 normal-case tracking-normal">· recommended 1080×1080</span></Label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="h-24 w-24 rounded-full overflow-hidden hairline bg-black/40 shrink-0">
                  {form.avatar_url && (
                    <img
                      src={form.avatar_url.startsWith("http") ? form.avatar_url : `${BACKEND_URL}${form.avatar_url}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <label
                  className="cursor-pointer text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors"
                  data-testid="profile-avatar-upload-label"
                >
                  Upload avatar
                  <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-upload-input" />
                </label>
                {form.avatar_url && (
                  <button type="button" onClick={() => set("avatar_url", "")} className="text-xs text-zinc-500 hover:text-nexus-pink" data-testid="profile-avatar-clear">
                    Remove
                  </button>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Cover image <span className="text-zinc-600 normal-case tracking-normal">· recommended 1920×640</span></Label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="h-24 w-40 rounded-lg overflow-hidden hairline bg-black/40 shrink-0">
                  {form.cover_url && (
                    <img
                      src={form.cover_url.startsWith("http") ? form.cover_url : `${BACKEND_URL}${form.cover_url}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <label
                  className="cursor-pointer text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors"
                  data-testid="profile-cover-upload-label"
                >
                  Upload cover
                  <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="profile-cover-upload-input" />
                </label>
                {form.cover_url && (
                  <button type="button" onClick={() => set("cover_url", "")} className="text-xs text-zinc-500 hover:text-nexus-pink">
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Tagline</Label>
              <Input data-testid="profile-tagline-input" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="mt-1.5 bg-white/[0.02] border-white/10 text-white" placeholder="Beauty & lifestyle creator" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Location</Label>
              <Input data-testid="profile-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5 bg-white/[0.02] border-white/10 text-white" placeholder="London, UK" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Total followers</Label>
              <div className="mt-1.5 flex items-center gap-2 p-2.5 rounded-md bg-white/[0.02] border border-white/10">
                <span className="font-display font-bold text-lg tabular-nums nexus-gradient-text" data-testid="profile-followers-value">
                  {form.follower_count ? Number(form.follower_count).toLocaleString() : "—"}
                </span>
                <button
                  type="button"
                  onClick={syncFollowers}
                  disabled={syncing}
                  data-testid="sync-followers-btn"
                  className="ml-auto text-[10px] uppercase tracking-widest text-nexus-blue hover:text-white inline-flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing…" : "Sync from Instagram"}
                </button>
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">
                Auto-pulled from your connected account in <a href="/growth" className="text-nexus-blue hover:underline">Growth AI</a>.
              </p>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Bio</Label>
            <Textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={4} className="mt-1.5 bg-white/[0.02] border-white/10 text-white" placeholder="Tell brands about you, your audience and your best work…" />
          </div>
        </section>

        <section className="glass rounded-2xl p-6 space-y-5">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Niches</div>
          <div className="flex flex-wrap gap-2">
            {NICHES.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => set("niches", toggle(form.niches, n))}
                data-testid={`profile-niche-${n}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  form.niches.includes(n) ? "border-nexus-purple bg-nexus-purple/15 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="text-xs uppercase tracking-widest text-zinc-400 pt-2">Platforms</div>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => set("platforms", toggle(form.platforms, p))}
                data-testid={`profile-platform-${p}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  form.platforms.includes(p) ? "border-nexus-blue bg-nexus-blue/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </section>

        <section className="glass rounded-2xl p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Portfolio</div>
          <MediaUploader
            items={form.portfolio}
            onChange={(next) => set("portfolio", next)}
            max={12}
            testId="portfolio-uploader"
          />
        </section>

        <section className="glass rounded-2xl p-6 space-y-4" data-testid="drafts-section">
          <DraftsPanel
            onExportUpload={(exportedFile) => {
              // When a resumed draft is exported, push it onto the portfolio.
              // Upload happens inside DraftsPanel's editor via MediaUploader-style handoff.
              // Here we just re-fetch the profile so the new item is reflected.
              // The exported file was NOT auto-uploaded (DraftsPanel doesn't do that),
              // so we upload it now.
              uploadFile(exportedFile)
                .then((r) => set("portfolio", [...(form.portfolio || []), r.url]))
                .catch(() => {});
            }}
          />
        </section>

        <section className="glass rounded-2xl p-6 space-y-4" data-testid="languages-section">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400">Languages you speak</div>
            <p className="text-xs text-zinc-500 mt-1">Brands filter creators by language for global campaigns.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["English", "Spanish", "French", "German", "Italian", "Portuguese", "Arabic", "Hindi", "Mandarin", "Japanese", "Korean"].map((lang) => {
              const on = (form.languages || []).includes(lang);
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => set("languages", on ? form.languages.filter((l) => l !== lang) : [...(form.languages || []), lang])}
                  data-testid={`language-${lang}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "border-nexus-blue bg-nexus-blue/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}
                >
                  {lang}
                </button>
              );
            })}
          </div>
        </section>

        <section className="glass rounded-2xl p-6 space-y-4" data-testid="rate-card-section">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400">Rate card <span className="text-zinc-500 normal-case tracking-normal">· optional</span></div>
            <p className="text-xs text-zinc-500 mt-1">Signal your pricing so brands come prepared. Round numbers signal confidence. Leave blank for &ldquo;DM to chat&rdquo;.</p>
          </div>
          <div className="space-y-2">
            {(form.rate_card || []).map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px_44px] gap-2 items-center" data-testid={`rate-card-row-${i}`}>
                <Input
                  value={r.package || ""}
                  onChange={(e) => set("rate_card", form.rate_card.map((row, idx) => idx === i ? { ...row, package: e.target.value.slice(0, 80) } : row))}
                  placeholder="e.g. IG Reel + Story frame"
                  className="bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid={`rate-card-package-${i}`}
                />
                <Input
                  value={r.price || ""}
                  onChange={(e) => set("rate_card", form.rate_card.map((row, idx) => idx === i ? { ...row, price: e.target.value.slice(0, 24) } : row))}
                  placeholder="£450"
                  className="bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid={`rate-card-price-${i}`}
                />
                <button
                  type="button"
                  onClick={() => set("rate_card", form.rate_card.filter((_, idx) => idx !== i))}
                  className="h-10 w-11 rounded-lg border border-white/10 text-zinc-500 hover:text-red-300 hover:border-red-300/40 transition-colors"
                  data-testid={`rate-card-remove-${i}`}
                  aria-label="Remove rate"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set("rate_card", [...(form.rate_card || []), { package: "", price: "" }])}
              className="w-full h-11 rounded-xl border-2 border-dashed border-white/10 hover:border-nexus-pink/40 text-zinc-400 hover:text-white text-sm transition-colors"
              data-testid="rate-card-add"
            >
              + Add a package
            </button>
          </div>
        </section>

        <section className="glass rounded-2xl p-6 space-y-4" data-testid="social-handles-section">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-400">Social handles</div>
            <p className="text-xs text-zinc-500 mt-1">
              Paste the full URL to each of your profiles. Brands click straight through to your work.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourhandle" },
              { key: "tiktok",    label: "TikTok",    placeholder: "https://tiktok.com/@yourhandle" },
              { key: "youtube",   label: "YouTube",   placeholder: "https://youtube.com/@yourchannel" },
              { key: "x",         label: "X (Twitter)", placeholder: "https://x.com/yourhandle" },
            ].map((s) => (
              <div key={s.key}>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{s.label}</Label>
                <Input
                  value={form.social_handles?.[s.key] || ""}
                  onChange={(e) => setSocial(s.key, e.target.value.trim())}
                  placeholder={s.placeholder}
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white"
                  data-testid={`social-${s.key}-input`}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
